import React, { useRef, useState } from "react";
import styled from "styled-components/macro";
import Dialog from "@fluentui/react/lib/Dialog";
import { ResourceStrings } from "../../ResourceStrings";
import Flex from "./Flex";
import Button from "./Button";
import { copyToClipboard } from "../../utils/react";
import { App } from "../../app";

export interface ShareLinkDialogProps {
    title: string;
    message: string;
    url: string;
    isOpen: boolean;
    onDismiss?: () => void;
}

export default function ShareLinkDialog(props: ShareLinkDialogProps): JSX.Element {
    const refUrlToCopy = useRef<HTMLTextAreaElement>(null);
    const refCopyBtn = useRef(null);
    const [error, setError] = useState<string>(null);

    const onDismiss = (): void => {
        setError(null);
        props.onDismiss();
    }

    const copyClicked = (): void => {
        if (copyToClipboard(refUrlToCopy)) {
            App.read("Copied to clipboard");
            onDismiss();
        } else {
            // Shouldn't happen but just in case
            const error = "Error while copying to clipboard. Please copy manually.";
            setError(error);
            App.read(error);
        }
    }

    return <DialogWrapper
        hidden={!props.isOpen}
        onDismiss={onDismiss}
        modalProps={{
            layerProps: {
                onLayerDidMount: () => {
                    setTimeout(() => {
                        refCopyBtn.current?.focus();
                    }, 100);
                }
            },
            isBlocking: false,
            styles: {
                main: {
                    border: `solid 2px gray`,
                    borderRadius: '.8rem',
                }
            }
        }}
        dialogContentProps={{
            showCloseButton: true,
            title: props.title,
            closeButtonAriaLabel: ResourceStrings.close(),
        }}
    >
        <div
            id="notificationMessageReader"
            className="visually-hidden"
            role={"status"}
            aria-live="assertive"
        >
            <div>{props.title} {props.message} {props.url}</div>
        </div>

        <Flex column densityVer="relaxed">
            <div>{props.message}</div>
            <textarea ref={refUrlToCopy} readOnly tabIndex={-1} className="visually-hidden" aria-hidden="true" value={props.url} />
            <div className="url" tabIndex={0}>{props.url}</div>

            {error && <div className="error">{error}</div>}
            <Flex justifyContent="flex-end" densityHor="normal">
                <Button onClick={onDismiss} buttonType="normal">Close</Button>
                <Button ref={refCopyBtn} onClick={copyClicked}>Copy to clipboard</Button>
            </Flex>
        </Flex>
    </DialogWrapper>
}

const DialogWrapper = styled(Dialog)`
    .ms-Dialog-main {
        min-width: 20rem;
        max-width: 90rem;
    }

    .url {
        border: solid 1px var(--color-on-surface-lighter);
        padding: .5rem 1rem;
        margin-bottom: 3rem;
    }
`;
